/* eslint-disable no-param-reassign */
import * as Sentry from '@sentry/react';
import axios, { AxiosInstance } from 'axios';
import urls from 'configs/urls';
import { AppBridge } from 'infrastructure/appBridge/appBridge';
import { APP_BRIDGE_CALLBACKS } from 'infrastructure/appBridge/appCallBacks';
import { getPartnerName } from 'partners';
import { getLocalStorageItem, truncateObject } from 'utils/commonFunctions';
import {
  clearSession,
  getSessionStorageItem,
  refreshSession,
  setSessionStorageItem
} from 'utils/sessionFunctions';
import { isRunningInWebView } from 'utils/webviewHelpers';
import { waitForSession } from 'utils/sessionGate';
import { v4 as uuidv4 } from 'uuid';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * Session-establishment requests (CreateSession, the one-time-token mint)
     * set this to bypass the webview session gate — they run BEFORE the session
     * exists, so awaiting the gate would deadlock — and to opt out of the
     * 401-closes-the-webview handling so AppBridge can retry with a fresh token.
     */
    skipSessionGate?: boolean;
  }
}

const homeSecurityPortalApi: AxiosInstance = axios.create({
  baseURL: urls.baseUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

homeSecurityPortalApi.interceptors.request.use(async (request) => {
  // Webview boot: hold every data call until CreateSession has set the session
  // cookie. Resolved by default, so non-webview flows pass straight through; a
  // failed session rejects the gate and every queued request rejects with it.
  if (!request.skipSessionGate) {
    await waitForSession();
  }

  const partnerName = getPartnerName();
  const userLanguage = getLocalStorageItem('i18nextLng');

  request.baseURL = urls.baseUrl;
  request.headers['Partner-ID'] = partnerName;
  request.headers['Client-ID'] = 'home-user-portal';
  request.headers['Accept-Language'] = userLanguage || 'en-US';
  request.headers['Accept-Version'] = 'v3';

  let sessionID = getSessionStorageItem('session_id');
  if (!sessionID) {
    sessionID = refreshSession();
    setSessionStorageItem('session_id', sessionID);
  }
  request.headers['X-Session-ID'] = sessionID;
  request.headers['X-Request-ID'] = uuidv4();

  return request;
});

homeSecurityPortalApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      // Session-establishment calls own their 401 handling (AppBridge retries
      // once with a force-refreshed token); closing the webview here would kill
      // the boot before the retry can run.
      if (
        (error.response.status === 401 || error.response.status === 403) &&
        !error.config?.skipSessionGate
      ) {
        if (isRunningInWebView()) {
          clearSession();
          try {
            AppBridge.sendMessageToApp(
              APP_BRIDGE_CALLBACKS.CLOSE_IN_APP_WEBVIEW
            );
          } catch (err) {
            console.error('Error closing WebView:', err);
          }
        } else {
          // Dynamic import to avoid a circular dependency with store.ts
          const { LogoutUser } = await import('store/store');
          LogoutUser();
          clearSession();
        }
      }
    } else {
      console.error('Network/Request Error:', error.message);
    }

    const config = error.config || {};
    const response = error.response || {};

    Sentry.addBreadcrumb({
      category: 'http-error',
      message: `${config.method?.toUpperCase()} ${config.url} → ${
        response.status || 'NO_RESPONSE'
      }`,
      data: {
        status: response.status,
        url: config.url,
        requestData: truncateObject(config.data, 100),
        responseData: truncateObject(response.data, 100),
        headers: truncateObject(config.headers, 200)
      },
      level: 'debug'
    });

    return Promise.reject(error);
  }
);

export { homeSecurityPortalApi };
